import { Component, computed, effect, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardListWidget, isHistoryWidgetSettings } from 'dashboard/interfaces/visuals/list-widget.interface';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { camelCase } from 'lodash-es';
import { TooltipDirective } from 'shared/directives/tooltip.directive';
import { VisualsDashboardPeriodPickerComponent } from '../../visuals-dashboard-period-picker/visuals-dashboard-period-picker.component';
import { VisualsDashboardSizePickerComponent } from '../../visuals-dashboard-size-picker/visuals-dashboard-size-picker.component';

@Component({
    selector: 'app-visual-dashboard-item-picker-item',
    imports: [TranslateModule, TooltipDirective, VisualsDashboardSizePickerComponent, VisualsDashboardPeriodPickerComponent],
    templateUrl: './visual-dashboard-item-picker-item.component.html',
    styleUrl: './visual-dashboard-item-picker-item.component.scss',
})
export class VisualDashboardItemPickerItemComponent {
    widget = input.required<DashboardListWidget>();

    isExpanded = signal(false);

    widgetImagePath = computed(() => {
        return '';
    });

    camelCaseKey = computed(() => {
        return camelCase(this.widget().key);
    });

    widgetTitle = computed(() => {
        return `dashboard.visuals.widgets.${this.camelCaseKey()}.title`;
    });

    widgetImageSrc = computed(() => {
        return `assets/images/visuals-dashboard-widgets/${this.camelCaseKey()}.png`;
    });

    onLangChange = toSignal(this.translateService.onLangChange);

    widgetDisplayDescription = computed(() => {
        const description = this.widget().translatedFullDescription;
        const isExpanded = this.isExpanded();

        if (!description || isExpanded) {
            return description;
        }

        const truncateLength = 100;
        return description.length > truncateLength ? description.substring(0, truncateLength) + '...' : description;
    });

    shouldShowToggleButton = computed(() => {
        return this.widget().translatedFullDescription.length > 100;
    });

    hasMultipleSizes = computed(() => {
        return this.widget().sizes.length > 1;
    });

    selectedSize = signal<VisualsDashboardWidgetSizingEnum | null>(null);

    defaultSize = computed(() => {
        const sizes = this.widget().sizes;
        return sizes.find((size) => size.isDefault)?.size || sizes[0]?.size;
    });

    currentSelectedSize = computed(() => {
        return this.selectedSize() ?? this.defaultSize();
    });

    hasPeriodSelection = computed(() => {
        const widget = this.widget();
        const defaultSettings = widget.defaultSettings;
        return defaultSettings && isHistoryWidgetSettings(defaultSettings);
    });

    selectedPeriod = signal<number | null>(null);

    inititalSelectedPeriod = computed(() => {
        const defaultSettings = this.widget().defaultSettings;
        if (isHistoryWidgetSettings(defaultSettings)) {
            return defaultSettings.optionValue;
        }
        return null;
    });

    constructor(
        private translateService: TranslateService,
        private visualsDashboardService: VisualsDashboardService,
    ) {
        effect(() => {
            const currentSize = this.currentSelectedSize();
            const widgetId = this.widget().widgetId;
            const selectedPeriod = this.selectedPeriod();

            untracked(() => {
                const selectedWidgets = this.visualsDashboardService.selectedWidgetsForAdding();
                const existingEntry = selectedWidgets.find((entry) => entry.widget.widgetId === widgetId);

                if (existingEntry && (existingEntry.selectedSize !== currentSize || existingEntry.selectedPeriod !== selectedPeriod)) {
                    this.visualsDashboardService.selectedWidgetsForAdding.set(
                        selectedWidgets.map((entry) =>
                            entry.widget.widgetId === widgetId
                                ? { widget: entry.widget, selectedSize: currentSize, selectedPeriod: selectedPeriod }
                                : entry,
                        ),
                    );
                }
            });
        });
    }

    onSizeSelected(size: VisualsDashboardWidgetSizingEnum): void {
        this.selectedSize.set(size);
    }

    onPeriodChange(periodValue: number | null): void {
        this.selectedPeriod.set(periodValue);
    }

    toggleWidgetSelection(): void {
        const selectedWidgets = this.visualsDashboardService.selectedWidgetsForAdding();
        const isAlreadySelected = selectedWidgets.some((entry) => entry.widget.widgetId === this.widget().widgetId);
        if (isAlreadySelected) {
            this.visualsDashboardService.selectedWidgetsForAdding.set(
                selectedWidgets.filter((entry) => entry.widget.widgetId !== this.widget().widgetId),
            );
        } else {
            this.visualsDashboardService.selectedWidgetsForAdding.set([
                ...selectedWidgets,
                { widget: this.widget(), selectedSize: this.currentSelectedSize(), selectedPeriod: this.selectedPeriod() },
            ]);
        }
    }

    toggleDescription(event: Event): void {
        event.stopPropagation();
        this.isExpanded.update((value) => !value);
    }

    resetSelection(): void {
        this.selectedSize.set(null);
    }
}
